import useUpdateUserMeta from "@/components/@common/hooks/useUpdateUserMeta";
import useDecryptNiceInfo from "@/components/receipt/hooks/useDecryptNiceInfo";
import { useAuthStore } from "@/lib/auth/AuthStore";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { CASH_INDEX_PAGE, SHOP_CHECKOUT_PAGE } from "@/settings/constant";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import { useDialogStore } from "src/store/useDialogStore";
import { getParams, toQueryString } from "src/utils/misc";
import { MAX_CASH_BALANCE, REDIRECT_FROM_AUTH_KEY } from "../CashCharge";
import useGetCashBalance from "./useGetCashBalance";
import useGetCashProducts from "./useGetCashProducts";
const useVerification = (param)=>{
    let { selectedCash , setIsVerificationDialogOpen  } = param;
    const router = useRouter();
    const { open , close  } = useDialogStore();
    const { isLoggedIn , user , updateUser  } = useAuthStore();
    const { data: cashBalance  } = useGetCashBalance();
    const { data: cashProducts  } = useGetCashProducts();
    const { mutate: decryptNice  } = useDecryptNiceInfo();
    const { mutateAsync: updateUserMeta  } = useUpdateUserMeta();
    const pendingVerificationRef = useRef(null);
    const processedParamsRef = useRef("");
    // 로그인 or 회원가입 성공 후 리다이렉트로 돌아왔을 때 처리
    useEffect(()=>{
        if (!isLoggedIn) return;
        const redirectFromAuth = localStorage.getItem(REDIRECT_FROM_AUTH_KEY) === "true";
        if (!redirectFromAuth) return;
        // 나이정보가 없는 경우: 본인인증 다이얼로그 표시
        if (!user.birth) {
            // 플래그 제거 (중복 실행 방지)
            localStorage.removeItem(REDIRECT_FROM_AUTH_KEY);
            localStorage.setItem("selectedCash", selectedCash);
            mixpanelEvent("identification Popup Viewed", {
                "Popup Title": "안전한 이용을 위해 본인 인증이 필요해요.",
                position: "cash",
                Category: "identification required"
            });
            setIsVerificationDialogOpen(true);
            return;
        }
        // 나이정보가 있는 경우: 한도 체크 후 결제 페이지로 이동
        const savedCashId = localStorage.getItem("selectedCash");
        if (savedCashId) {
            if ((cashProducts === null || cashProducts === void 0 ? void 0 : cashProducts.products) && cashProducts.products.length > 0 && cashBalance !== undefined) {
                // 플래그 제거 (중복 실행 방지)
                localStorage.removeItem(REDIRECT_FROM_AUTH_KEY);
                var _cashBalance_totalBalance;
                // 캐시 한도 체크
                const balance = (_cashBalance_totalBalance = cashBalance.totalBalance) !== null && _cashBalance_totalBalance !== void 0 ? _cashBalance_totalBalance : 0;
                const selectedProduct = cashProducts.products.find((product)=>product.id === Number(savedCashId));
                if (!selectedProduct) {
                    ;
                    localStorage.removeItem("selectedCash");
                    return;
                }
                const selectedCashAmount = selectedProduct.price;
                if (balance + selectedCashAmount > MAX_CASH_BALANCE) {
                    mixpanelEvent("Popup Viewed", {
                        Title: "캐시 한도를 초과했어요.",
                        position: "캐시 한도"
                    });
                    open({
                        id: "cash-balance-exceeded",
                        type: "confirm",
                        props: {
                            title: "캐시 한도를 초과했어요.",
                            description: "보유 캐시와 충전 예정 금액의 합이 한도 200만 원을 초과해요.\n일부 캐시를 사용 후 다시 시도해 주세요.",
                            onConfirm: ()=>{
                                close("cash-balance-exceeded");
                            },
                            contentProps: {
                                onInteractOutside: ()=>{
                                    close("cash-balance-exceeded");
                                }
                            }
                        }
                    });
                    localStorage.removeItem("selectedCash");
                    return;
                }
                // 결제 페이지로 이동
                router.push("".concat(SHOP_CHECKOUT_PAGE, "?id=").concat(savedCashId, "&orderType=cash"));
                localStorage.removeItem("selectedCash");
            }
        }
    }, [
        isLoggedIn,
        user.birth,
        selectedCash,
        router,
        setIsVerificationDialogOpen,
        cashProducts,
        cashBalance,
        open,
        close
    ]);
    // 본인인증 팝업 닫히면 캐시 페이지로 이동
    useEffect(()=>{
        if (!router.query.enc_data) return;
        const id = router.query.token_version_id;
        const data = router.query.enc_data;
        if (window.opener && window.opener.location) {
            const params = getParams(window.opener.location.search);
            const query = toQueryString({
                ...params,
                tokenVersionId: id,
                encData: data
            });
            window.opener.location.href = "".concat(window.location.origin, "/wallet/cash").concat(query);
            self.close();
        }
    }, [
        router.query
    ]);
    // 본인인증 성공 후 정보 업데이트
    useEffect(()=>{
        if (!router.query.enc_data || !router.query.token_version_id) return;
        const id = router.query.token_version_id;
        const data = router.query.enc_data;
        const paramsKey = "".concat(id, "-").concat(data);
        // 이미 처리한 파라미터인지 확인
        if (processedParamsRef.current === paramsKey) return;
        processedParamsRef.current = paramsKey;
        decryptNice({
            tokenVersionId: id,
            encData: data
        }, {
            onSuccess: async (data)=>{
                const birthYear = data.birth.slice(0, 4);
                await updateUserMeta({
                    birth: birthYear
                });
                updateUser({
                    ...user,
                    birth: birthYear
                });
                mixpanelEvent("identification Completed");
                const savedCashId = localStorage.getItem("selectedCash");
                if (savedCashId) {
                    // 리다이렉트 플래그 제거
                    localStorage.removeItem(REDIRECT_FROM_AUTH_KEY);
                    // 처리 대기 플래그 설정
                    pendingVerificationRef.current = {
                        savedCashId
                    };
                }
            },
            onError: ()=>{
                open({
                    id: "identification-failed",
                    type: "confirm",
                    props: {
                        title: "인증이 완료되지 않아 결제를 진행할 수 없어요.",
                        confirmText: "확인",
                        onConfirm: ()=>{
                            mixpanelEvent("identification Popup Viewed", {
                                "Popup Title": "인증이 완료되지 않아 결제를 진행할 수 없어요.",
                                position: "cash",
                                Category: "identification fail"
                            });
                            router.replace(CASH_INDEX_PAGE);
                            close("identification-failed");
                        }
                    }
                });
            }
        });
    }, [
        router.query.enc_data,
        router.query.token_version_id
    ]);
    // 본인인증 성공 후 한도 체크 or 결제 페이지로 이동
    useEffect(()=>{
        if (pendingVerificationRef.current && (cashProducts === null || cashProducts === void 0 ? void 0 : cashProducts.products) && cashProducts.products.length > 0 && cashBalance !== undefined) {
            const { savedCashId  } = pendingVerificationRef.current;
            pendingVerificationRef.current = null;
            var _cashBalance_totalBalance;
            // 캐시 한도 체크
            const balance = (_cashBalance_totalBalance = cashBalance.totalBalance) !== null && _cashBalance_totalBalance !== void 0 ? _cashBalance_totalBalance : 0;
            const selectedProduct = cashProducts.products.find((product)=>product.id === Number(savedCashId));
            if (!selectedProduct) {
                ;
                return;
            }
            const selectedCashAmount = selectedProduct.price;
            if (balance + selectedCashAmount > MAX_CASH_BALANCE) {
                mixpanelEvent("Popup Viewed", {
                    Title: "캐시 한도를 초과했어요.",
                    position: "캐시 한도"
                });
                open({
                    id: "cash-balance-exceeded",
                    type: "confirm",
                    props: {
                        title: "캐시 한도를 초과했어요.",
                        description: "보유 캐시와 충전 예정 금액의 합이 한도 200만 원을 초과해요.\n일부 캐시를 사용 후 다시 시도해 주세요.",
                        onConfirm: ()=>{
                            close("cash-balance-exceeded");
                        },
                        contentProps: {
                            onInteractOutside: ()=>{
                                close("cash-balance-exceeded");
                            }
                        }
                    }
                });
                return;
            }
            // 결제 페이지로 이동
            router.push("".concat(SHOP_CHECKOUT_PAGE, "?id=").concat(savedCashId, "&orderType=cash"));
            localStorage.removeItem("selectedCash");
        }
    }, [
        cashProducts,
        cashBalance,
        router,
        pendingVerificationRef.current
    ]);
};
export default useVerification;
